// proctoringSummary.js
//
// Turns one attempt's proctoring sessions into an at-a-glance summary for a recruiter.
//
// Design rules
//  * No score and no verdict. The page says "Nothing here is a verdict", so this only says
//    WHERE to look and WHY, using rules the recruiter can read.
//  * Every number comes from the sessions already on the page (normalised rows from
//    normalizeSession) plus the candidate's own counters, so nothing new is needed from the API.
//  * Thresholds live in THRESHOLDS. Change them in one place.
import {
  toMs, ROUND_ORDER, fmtClock, fmtSeconds, plural, roundLabel, questionLabel,
} from './proctoringShared';

export const THRESHOLDS = {
  longEpisodeS:   30,    // one episode at least this long is worth a look
  flaggedShare:   0.10,  // a flag was open for at least this share of the monitored time
  cameraWarnings: 5,     // candidate.proctoringViolations.cameraCount
  tabSwitches:    3,     // candidate.proctoringViolations.count
};

export const LEVEL_LABEL = {
  review: 'Needs human review',
  look:   'Worth a look',
  clear:  'Nothing notable',
  nodata: 'No camera data',
};

const NEXT_STEP = {
  nodata: 'There is no camera data to review. Rely on the test results.',
  clear:  'No camera follow-up needed. Decide on the test score and your usual next step.',
  look:   'Skim the hot spots and the matching timeline below. A flag on its own is not evidence, so weigh it with the test result.',
  review: 'Open the timeline and check the flagged items yourself before the camera data counts either way. If it is still unclear, consider a short follow-up conversation or a re-test.',
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const reasonText = (r) => String(r || 'unknown').replace(/[_-]+/g, ' ');
const pct = (x) => `${Math.round(x * 100)}%`;
const round1 = (x) => Math.round(x * 10) / 10;

const durationOf = (e) => {
  if (e.duration_s !== null && e.duration_s !== undefined && Number.isFinite(Number(e.duration_s))) {
    return Number(e.duration_s);
  }
  const a = toMs(e.start_t);
  const b = toMs(e.end_t);
  return Number.isFinite(a) && Number.isFinite(b) ? Math.max(0, (b - a) / 1000) : 0;
};

// Seconds during which at least one flag was open. Different reasons can overlap in time,
// so adding durations up would count the same moment twice.
const unionSeconds = (episodes) => {
  const spans = [];
  let loose = 0;                                   // no usable start time: just add its duration
  episodes.forEach((e) => {
    const d = durationOf(e);
    const a = toMs(e.start_t);
    if (!Number.isFinite(a)) { loose += d; return; }
    const b = toMs(e.end_t);
    spans.push([a, Number.isFinite(b) && b >= a ? b : a + d * 1000]);
  });
  spans.sort((x, y) => x[0] - y[0]);
  let total = 0;
  let curS = null;
  let curE = null;
  spans.forEach(([s, e]) => {
    if (curS === null) { curS = s; curE = e; }
    else if (s <= curE) { curE = Math.max(curE, e); }
    else { total += curE - curS; curS = s; curE = e; }
  });
  if (curS !== null) total += curE - curS;
  return total / 1000 + loose;
};

const whereOf = (round, questionIndex) =>
  [roundLabel(round), questionLabel(questionIndex)].filter(Boolean).join(' ');

/**
 * @param sessions       normalised sessions of ONE attempt (group.sessions)
 * @param violations     candidate.proctoringViolations
 * @param proctoring     candidate.proctoring
 * @param olderAttempts  number of earlier attempts (the summary covers the latest only)
 */
export function buildProctoringSummary({ sessions = [], violations = {}, proctoring = {}, olderAttempts = 0 } = {}) {
  const episodesOf = (s) => (Array.isArray(s.episodes) ? s.episodes : []);

  // ---- per round (display order, plus any round the order does not know about) ----
  const extraRounds = Array.from(new Set(sessions.map((s) => s.round))).filter((r) => !ROUND_ORDER.includes(r));
  const byRound = ROUND_ORDER.concat(extraRounds).map((round) => {
    const rs = sessions.filter((s) => s.round === round);
    const eps = rs.flatMap(episodesOf);
    const monitoredS = rs.reduce((t, s) => t + num(s.monitoredS), 0);
    const flaggedS = unionSeconds(eps);
    return {
      round,
      hasSession: rs.length > 0,
      monitoredS,
      flaggedS,
      episodes: eps.length,
      warnings: rs.reduce((t, s) => t + num(s.warnings), 0),
      share: monitoredS > 0 ? Math.min(flaggedS / monitoredS, 1) : null,
    };
  });
  const missingRounds = byRound.filter((r) => !r.hasSession).map((r) => r.round);

  // ---- every episode, tagged with its round ----
  const all = sessions.flatMap((s) => episodesOf(s).map((e) => ({ ...e, round: s.round, seconds: durationOf(e) })));

  // ---- by reason ----
  const reasonMap = new Map();
  all.forEach((e) => {
    const key = e.reason || 'unknown';
    const r = reasonMap.get(key) || { reason: key, episodes: 0, seconds: 0 };
    r.episodes += 1;
    r.seconds += e.seconds;
    reasonMap.set(key, r);
  });
  const reasonTotal = Array.from(reasonMap.values()).reduce((t, r) => t + r.seconds, 0);
  const byReason = Array.from(reasonMap.values())
    .map((r) => ({ ...r, seconds: round1(r.seconds), share: reasonTotal > 0 ? r.seconds / reasonTotal : null }))
    .sort((a, b) => b.seconds - a.seconds);

  // ---- hot spots: the questions where the most flagged time piled up ----
  const spotMap = new Map();
  all.forEach((e) => {
    const key = `${e.round}|${e.question_index === null || e.question_index === undefined ? '' : e.question_index}`;
    const s = spotMap.get(key) || { round: e.round, question_index: e.question_index, seconds: 0, episodes: 0, reasons: new Map() };
    s.seconds += e.seconds;
    s.episodes += 1;
    s.reasons.set(e.reason || 'unknown', (s.reasons.get(e.reason || 'unknown') || 0) + e.seconds);
    spotMap.set(key, s);
  });
  const hotSpots = Array.from(spotMap.values())
    .filter((s) => s.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds)
    .slice(0, 3)
    .map((s) => ({
      round: s.round,
      question_index: s.question_index,
      seconds: round1(s.seconds),
      episodes: s.episodes,
      topReason: Array.from(s.reasons.entries()).sort((a, b) => b[1] - a[1])[0][0],
    }));

  // ---- totals ----
  const monitoredS = byRound.reduce((t, r) => t + r.monitoredS, 0);
  const flaggedS = byRound.reduce((t, r) => t + r.flaggedS, 0);
  const flaggedShare = monitoredS > 0 ? Math.min(flaggedS / monitoredS, 1) : null;
  const longest = all.reduce((best, e) => (!best || e.seconds > best.seconds ? e : best), null);
  const totals = {
    monitoredS,
    flaggedS: round1(flaggedS),
    flaggedShare,
    episodes: all.length,
    warnings: byRound.reduce((t, r) => t + r.warnings, 0),
    longest: longest ? { seconds: round1(longest.seconds), round: longest.round, question_index: longest.question_index, reason: longest.reason } : null,
  };

  // ---- signals: each one is a rule the recruiter can read ----
  const cameraWarnings = num(violations.cameraCount);
  const tabSwitches = num(violations.count);
  const signals = [];
  if (proctoring.needs_human_review) {
    signals.push({ severity: 'review', text: 'The camera system marked this candidate for human review.' });
  }
  if (proctoring.override) {
    signals.push({ severity: 'review', text: 'Continued after a rejected calibration, so gaze verdicts need a human before they count.' });
  }
  if (violations.autoSubmitted) {
    signals.push({ severity: 'review', text: 'The test was auto-submitted after the tab-switch limit was reached.' });
  }
  if (totals.longest && totals.longest.seconds >= THRESHOLDS.longEpisodeS) {
    signals.push({
      severity: 'look',
      text: `Longest episode ${fmtSeconds(totals.longest.seconds)} (${reasonText(totals.longest.reason)}, ${whereOf(totals.longest.round, totals.longest.question_index)}).`,
    });
  }
  if (flaggedShare !== null && flaggedShare >= THRESHOLDS.flaggedShare) {
    signals.push({ severity: 'look', text: `${pct(flaggedShare)} of the monitored time had a flag open.` });
  }
  if (cameraWarnings >= THRESHOLDS.cameraWarnings) {
    signals.push({ severity: 'look', text: `${plural(cameraWarnings, 'camera warning')} in total.` });
  }
  if (tabSwitches >= THRESHOLDS.tabSwitches) {
    signals.push({ severity: 'look', text: `${tabSwitches} tab switch${tabSwitches === 1 ? '' : 'es'} in total.` });
  }

  const level = sessions.length === 0 ? 'nodata'
    : signals.some((s) => s.severity === 'review') ? 'review'
    : signals.length > 0 ? 'look'
    : 'clear';

  // ---- one-line headline ----
  let headline;
  if (level === 'nodata') {
    headline = 'No camera sessions were recorded for this candidate.';
  } else {
    const base = monitoredS > 0
      ? `The camera ran for ${fmtClock(monitoredS)} across ${plural(sessions.length, 'round')}.`
      : `${plural(sessions.length, 'round')} monitored (camera time not available yet).`;
    if (totals.episodes === 0) {
      headline = `${base} No episodes were flagged.`;
    } else {
      const share = flaggedShare === null ? '' : ` (${pct(flaggedShare)} of that time)`;
      const top = byReason[0] ? `, mostly ${reasonText(byReason[0].reason)}` : '';
      headline = `${base} ${plural(totals.episodes, 'episode')} ${totals.episodes === 1 ? 'was' : 'were'} flagged, `
        + `covering ${fmtSeconds(totals.flaggedS)}${share}${top}. `
        + `Longest: ${fmtSeconds(totals.longest.seconds)} in ${whereOf(totals.longest.round, totals.longest.question_index)}.`;
    }
  }

  return {
    level,
    levelLabel: LEVEL_LABEL[level],
    headline,
    signals,
    nextStep: NEXT_STEP[level],
    totals,
    byReason,
    byRound,
    hotSpots,
    missingRounds,
    olderAttempts,
  };
}

// Plain text for pasting into an ATS note or an email to the hiring manager.
export function summaryToText(summary, candidateName) {
  const lines = [
    `Camera proctoring summary${candidateName ? `: ${candidateName}` : ''}`,
    `Level: ${summary.levelLabel} (a prompt for where to look, not a verdict)`,
    summary.headline,
  ];
  if (summary.signals.length) {
    lines.push('', 'Why:');
    summary.signals.forEach((s) => lines.push(`- ${s.text}`));
  }
  if (summary.byReason.length) {
    lines.push('', 'By reason:');
    summary.byReason.forEach((r) => lines.push(`- ${reasonText(r.reason)}: ${plural(r.episodes, 'episode')}, ${fmtSeconds(r.seconds)}`));
  }
  if (summary.hotSpots.length) {
    lines.push('', 'Hot spots:');
    summary.hotSpots.forEach((h) => lines.push(`- ${whereOf(h.round, h.question_index)}: ${fmtSeconds(h.seconds)}, mostly ${reasonText(h.topReason)}`));
  }
  if (summary.missingRounds.length && summary.level !== 'nodata') {
    lines.push('', `No camera session for: ${summary.missingRounds.map((r) => roundLabel(r)).join(', ')} (skipped for this role, or the camera was off).`);
  }
  if (summary.olderAttempts > 0) {
    lines.push('', `Covers the latest attempt only. ${plural(summary.olderAttempts, 'earlier attempt')} not included.`);
  }
  lines.push('', `Next step: ${summary.nextStep}`);
  return lines.join('\n');
}